import { useEffect, useMemo } from "react";

import { DeviceFrame } from "@/components/device-frame";
import { PreviewBody, type PreviewBodyData } from "@/components/preview-body";
import { Spinner } from "@/components/ui/spinner";
import type { DraftPreview } from "@/graphql";

interface DraftPreviewPanelProps {
  draftPreview: DraftPreview;
  /** When true, renders the redirect-mode notice. Defaults to true. */
  showRedirectNotice?: boolean;
  isSaving?: boolean;
}

/**
 * Live preview of a draft, rendered inside a phone-shaped frame and with
 * the customer-supplied custom CSS injected as a global <style> tag.
 *
 * Replaces the legacy <Preview/> component (react-helmet + react-sticky +
 * withDevice + GET_GALLERY) by leaning on PreviewBody for rendering and
 * a useEffect for global style injection.
 */
export function DraftPreviewPanel({
  draftPreview,
  showRedirectNotice = true,
  isSaving = false,
}: DraftPreviewPanelProps) {
  // Inject the customer's custom CSS into <head> so it can target the
  // legacy global preview class names. Cleaned up on unmount + on re-render.
  useEffect(() => {
    if (!draftPreview.css) return;
    const styleEl = document.createElement("style");
    styleEl.setAttribute("data-sked-preview-css", "draft");
    styleEl.textContent = draftPreview.css;
    document.head.appendChild(styleEl);
    return () => {
      document.head.removeChild(styleEl);
    };
  }, [draftPreview.css]);

  const data: PreviewBodyData = useMemo(
    () => ({
      instagramUsername: draftPreview.instagramUsername,
      instagramProfileImageUrl: draftPreview.instagram?.profileImageUrl ?? null,
      hasProfileHeader: draftPreview.hasProfileHeader,
      hasIntroduction: draftPreview.hasIntroduction,
      introductionText: draftPreview.introductionText,
      buttons: draftPreview.buttons,
      hasGallery: draftPreview.hasGallery,
      galleryTitle: draftPreview.galleryTitle,
      galleryId: draftPreview.galleryId,
      hasRedirect: draftPreview.hasRedirect,
      galleryRedirect: draftPreview.galleryRedirect,
      style: draftPreview.style,
      css: draftPreview.css,
    }),
    [draftPreview]
  );

  return (
    <div className="rounded-[28px] border border-neutral-100 bg-white p-4 shadow-sm sm:p-5">
      <div className="mb-4 space-y-1 text-center xl:text-left">
        <p className="font-inter text-xs font-semibold uppercase tracking-[0.08em] text-neutral-500">
          Live preview
        </p>
        <p className="font-inter text-sm text-neutral-600">
          Review the published page layout and styling before you push changes
          live.
        </p>
      </div>
      <div className="sked-preview relative flex justify-center">
        {isSaving ? (
          <div className="pointer-events-none absolute left-1/2 top-4 z-10 -translate-x-1/2 rounded-full border border-neutral-100 bg-white px-3 py-1.5 shadow-sm">
            <div className="flex items-center gap-2 font-inter text-xs font-medium text-neutral-600">
              <Spinner size="sm" />
              Saving preview...
            </div>
          </div>
        ) : null}
        <DeviceFrame>
          <PreviewBody data={data} showRedirectNotice={showRedirectNotice} />
        </DeviceFrame>
      </div>
    </div>
  );
}
